import { useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  PatchUsersRequest,
  PatchUsersResponse,
  PermissionLevel,
  UserInfo,
} from '../api/resources/userManagement/types';
import useRequiredParams from '../hooks/useRequiredParams';
import { Role } from '../types/resources/userManagement';

type UseUserManagementProps = {
  api: {
    getUsers: (accountId: string) => Promise<UserInfo[]>;
    patchUsers: (
      body: Omit<PatchUsersRequest, 'account_id'>,
    ) => Promise<PatchUsersResponse>;
    deleteUsers: (usersEmail: string[]) => Promise<any>;
  };
  isEnabled: boolean;
  currentUserEmail?: string;
};

const emptyUsersForAccount: UserInfo[] = [];

const useUserManagement = ({
  api,
  isEnabled,
  currentUserEmail,
}: UseUserManagementProps) => {
  const { accountId } = useRequiredParams<{ accountId: string }>();
  const queryClient = useQueryClient();

  const queryKey = ['users', accountId];

  // TODO: passing in account ID because if we do not go via the landing page then the account Id isn't set properly
  // Might get fixed as part of Next js migration so double check this
  const { data: usersForAccount, isFetching: isLoadingUsersInfo } = useQuery<
    UserInfo[]
  >(queryKey, () => api.getUsers(accountId), {
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!accountId && isEnabled,
    onSuccess: () => {
      resetUsersMutationState();
    },
  });

  const {
    mutate: createUsers,
    isLoading: isCreatingUsers,
    isSuccess: isCreateSuccess,
    reset: resetCreateUsersMutationState,
    data: createUsersData,
  } = useMutation<PatchUsersResponse, Error, PatchUsersRequest>(
    args => api.patchUsers(args),
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    },
  );

  const {
    mutate: patchUsers,
    isLoading: isPatchingUsers,
    isSuccess: isPatchSuccess,
    reset: resetPatchUsersMutationState,
    data: patchUsersData,
  } = useMutation<PatchUsersResponse, Error, PatchUsersRequest>(
    args => api.patchUsers(args),
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    },
  );

  const {
    mutate: deleteUsers,
    isLoading: isDeletingUsers,
    isSuccess: isDeleteSuccess,
    reset: resetDeleteUsersMutationState,
    variables: usersToDelete,
  } = useMutation<{ success: boolean }, Error, string[]>(
    usersEmail => api.deleteUsers(usersEmail),
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    },
  );

  const currentUserRole = useMemo(() => {
    // all user role emails are stored in lowercase
    const sanitisedUserEmail = currentUserEmail?.toLowerCase();
    const currUserDetails = usersForAccount?.find(
      user => user.user_email === sanitisedUserEmail,
    );

    return currUserDetails?.role;
  }, [usersForAccount, currentUserEmail]);

  // Used to reset the mutation state when cycling through the user table for different accounts
  // so that any notifications from previous mutations are removed
  const resetUsersMutationState = () => {
    resetCreateUsersMutationState();
    resetDeleteUsersMutationState();
    resetPatchUsersMutationState();
  };

  // hack to get agentic kitchen group permissions
  // in long run we should revisit the permission model to be more granular.
  const isAgenticKitchenUser = useMemo(() => {
    // all user role emails are stored in lowercase
    const sanitisedUserEmail = currentUserEmail?.toLowerCase();
    const currUserDetails = usersForAccount?.find(
      user => user.user_email === sanitisedUserEmail,
    );
    return currUserDetails?.tags.some(t => t === 'agentic-kitchen');
  }, [usersForAccount, currentUserEmail]);

  const userPermissionsPerProject = useMemo(() => {
    if (!usersForAccount) {
      return {};
    }

    const permissions: Record<
      string,
      Record<string, Record<string, PermissionLevel>>
    > = {};

    usersForAccount.forEach(user => {
      const userEmail = user.user_email.toLowerCase();
      permissions[userEmail] = {};

      Object.entries(user.per_project_config || {}).forEach(
        // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
        ([projectId, config]) => {
          permissions[userEmail][projectId] = {};

          // Check if V2 fields exist and have values
          const hasReadFeatures =
            config.read_features && config.read_features.length > 0;
          const hasWriteFeatures =
            config.write_features && config.write_features.length > 0;

          if (hasReadFeatures || hasWriteFeatures) {
            // V2: Use read_features and write_features arrays
            const writeFeaturesSet = new Set(config.write_features || []);
            const readFeaturesSet = new Set(config.read_features || []);

            // Collect all features mentioned in either array
            const allFeatures = new Set([
              ...(config.write_features || []),
              ...(config.read_features || []),
            ]);

            allFeatures.forEach(feature => {
              if (writeFeaturesSet.has(feature)) {
                permissions[userEmail][projectId][feature] = 'edit';
              } else if (readFeaturesSet.has(feature)) {
                permissions[userEmail][projectId][feature] = 'read';
              } else {
                permissions[userEmail][projectId][feature] = 'none';
              }
            });
          } else {
            // V1: Fallback to role and feature_enabled
            const role = config.role;
            const enabledFeatures = config.feature_enabled || [];

            // Map role to permission level
            let rolePermission: 'read' | 'edit' = 'read';
            if (role === Role.EDITOR) {
              rolePermission = 'edit';
            } else if (role === Role.VIEWER) {
              rolePermission = 'read';
            }

            // For each feature in feature_enabled, assign the role permission
            enabledFeatures.forEach(feature => {
              permissions[userEmail][projectId][feature] = rolePermission;
            });
          }
        },
      );
    });

    return permissions;
  }, [usersForAccount]);

  return {
    usersForAccount: usersForAccount || emptyUsersForAccount,
    currentUserRole,
    isLoadingUsersInfo,
    createUsers,
    createUsersData,
    isCreatingUsers,
    isCreateSuccess,
    patchUsers,
    patchUsersData,
    isPatchingUsers,
    isPatchSuccess,
    deleteUsers,
    usersToDelete,
    isDeletingUsers,
    isDeleteSuccess,
    isAgenticKitchenUser,
    userPermissionsPerProject,
  };
};

export default useUserManagement;
